import { CompositionType } from '~/types/model.types';
import {
    CompanyInfoLayoutType,
    CompositionContentType,
    CompositionLayoutType,
    TCompositionContentTypesWithoutCompanyInfo
} from '~/types/builder.types';
import {
    CompanyInfoSidebarScreen,
    ContainerSidebarScreen
} from '~/components/Builder/BuilderSidebar/builder-sidebar.types';
import { getCompanyInfoLayouts } from './company-info-layout.utils';
import { getCompositionLayouts } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.utils';
import type { RootStore } from '~/stores/root.store';
import { ICompositionLayoutTypeConfig } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import { Flow } from '~/models/flow.model';

interface ICompositionConfig {
    getLayoutsFn: (...params: any) => {
        layouts:
            | Record<CompanyInfoLayoutType, ICompositionLayoutTypeConfig>
            | Partial<
                  Record<CompositionLayoutType, ICompositionLayoutTypeConfig>
              >;
        defaultLayout: ICompositionLayoutTypeConfig;
    };
    sidebarLayout:
        | CompanyInfoSidebarScreen
        | ((
              compositionContentType: CompositionContentType
          ) => ContainerSidebarScreen);
}

const COMPOSITION_CONTENT_TYPE_TO_SIDEBAR: Record<
    TCompositionContentTypesWithoutCompanyInfo,
    ContainerSidebarScreen
> = {
    [CompositionContentType.Composition]:
        ContainerSidebarScreen.SelectCompositionLayout,
    [CompositionContentType.Question]:
        ContainerSidebarScreen.SelectQuestionComposition,
    [CompositionContentType.Image]:
        ContainerSidebarScreen.SelectImageComposition,
    [CompositionContentType.CanvaImage]:
        ContainerSidebarScreen.SelectImageComposition,
    [CompositionContentType.Text]: ContainerSidebarScreen.SelectTextComposition,
    [CompositionContentType.Video]:
        ContainerSidebarScreen.SelectVideoComposition
};

export const COMPOSITION_CONFIG_BY_TYPE: Record<
    CompositionType,
    ICompositionConfig
> = {
    [CompositionType.CompanyInfo]: {
        getLayoutsFn: getCompanyInfoLayouts,
        sidebarLayout: CompanyInfoSidebarScreen.SelectLayout
    },
    [CompositionType.Default]: {
        getLayoutsFn: getCompositionLayouts,
        sidebarLayout: compositionContentType =>
            COMPOSITION_CONTENT_TYPE_TO_SIDEBAR[compositionContentType]
    }
};

export const COMPOSITION_TYPE_BY_SIDEBAR_LAYOUT: Partial<
    Record<ContainerSidebarScreen & CompanyInfoSidebarScreen, CompositionType>
> = {
    [CompanyInfoSidebarScreen.Main]: CompositionType.CompanyInfo,
    [ContainerSidebarScreen.SelectCompositionLayout]: CompositionType.Default
};

export function getDefaultCompositionLayout(
    rootStore: RootStore,
    compositionType: CompositionType,
    compositionContentType: CompositionContentType,
    compositionLayoutType?: CompositionLayoutType,
    selectedFlow?: Flow
) {
    const compositionConfig = COMPOSITION_CONFIG_BY_TYPE[compositionType];

    if (!compositionConfig) {
        return null;
    }

    const { getLayoutsFn } = compositionConfig;

    const params =
        compositionType === CompositionType.CompanyInfo
            ? rootStore.companyStore.currCompany
            : {
                  compositionContentType,
                  compositionLayoutType,
                  getAllLayoutTypes: true,
                  selectedFlow
              };

    const { defaultLayout } = getLayoutsFn(params);

    return defaultLayout.layoutStruct;
}
